import { useState, type FormEvent } from 'react'
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { MinimalAlert, MinimalButton, MinimalInput, MinimalSegmentedControl } from '@ovasabi/ui-minimal'
import * as S from './OpsStyle'
import { OpsPromotionsPage } from '../promotions/PromotionsPage'
import { OpsOverviewPage } from './OpsOverviewPage'
import { OpsOrdersPage } from './OpsOrdersPage'
import { OpsReceivablesPage } from './OpsReceivablesPage'
import { OpsSupportPage } from './OpsSupportPage'
import { OpsVerificationPage } from './OpsVerificationPage'
import { OpsVendorsPage } from './OpsVendorsPage'
import { OpsUsersPage } from './OpsUsersPage'
import { OpsCategoriesPage } from './OpsCategoriesPage'
import { OpsMessagesPage } from './OpsMessagesPage'
import { useOpsSession, type OperatorRole, type OpsSession } from './opsSession'

/**
 * Admin + customer-relations console, mounted at /ops.
 *
 * Hidden, not secret: nothing in either app links here, and the route is not in
 * any navigation — but the protection that matters is the backend's. Every
 * action this console performs goes through a command the server marks
 * admin-permission and the authorizer checks against the session's capabilities.
 * A customer who types /ops sees the sign-in form and gets nowhere with it.
 */

interface NavItem {
  path: string
  label: string
  /** Match the path exactly — the console root would otherwise match everything. */
  exact?: boolean
}

const NAV: readonly NavItem[] = [
  { path: '/ops', label: 'Today', exact: true },
  { path: '/ops/people', label: 'People' },
  { path: '/ops/kitchens', label: 'Kitchens' },
  { path: '/ops/verification', label: 'Verification' },
  { path: '/ops/categories', label: 'Meal categories' },
  { path: '/ops/orders', label: 'Orders' },
  { path: '/ops/receivables', label: 'Receivables' },
  { path: '/ops/support', label: 'Support' },
  { path: '/ops/messages', label: 'Messages' },
  { path: '/ops/promotions', label: 'Promotions' },
]

const OpsSignIn = ({ session }: { session: OpsSession }) => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [role, setRole] = useState<OperatorRole>('ops')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (busy) return
    setBusy(true)
    setError('')
    try {
      await session.signIn(email, password, role)
    } catch (err) {
      // Deliberately non-specific: this form is reachable by anyone, and it
      // should not report whether an address exists.
      setError(
        err instanceof Error && /operator access/i.test(err.message)
          ? err.message
          : 'Those credentials were not accepted.',
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <S.LoginWrap>
      <S.LoginCard onSubmit={submit}>
        <S.H1>
          Choose<S.BrandMark>Chow</S.BrandMark> operations
        </S.H1>
        <S.Lede>Sign in with an operator account.</S.Lede>
        <MinimalInput label="Email" type="email" autoComplete="username" value={email}
          onChange={(event) => setEmail(event.target.value)} required disabled={busy} />
        <MinimalInput label="Password" type="password" autoComplete="current-password" value={password}
          onChange={(event) => setPassword(event.target.value)} required disabled={busy} />
        <MinimalSegmentedControl<OperatorRole> ariaLabel="Operator role" value={role} onChange={setRole}
          disabled={busy} variant="brand" options={[
            { value: 'ops', label: 'Operations' }, { value: 'support', label: 'Support' },
          ]} />
        {error && <MinimalAlert tone="danger" title="Sign-in unsuccessful">{error}</MinimalAlert>}
        <MinimalButton type="submit" disabled={busy} fullWidth>
          {busy ? 'Signing in…' : 'Sign in'}
        </MinimalButton>
      </S.LoginCard>
    </S.LoginWrap>
  )
}

export const OpsApp = () => {
  const session = useOpsSession()
  const location = useLocation()
  const navigate = useNavigate()

  if (session.loading) return <S.Page><S.Main><MinimalAlert tone="info" title="Loading operations">Checking your session…</MinimalAlert></S.Main></S.Page>
  if (!session.user) return <OpsSignIn session={session} />

  const isActive = (item: NavItem) =>
    item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path)

  return (
    <S.Page>
      <S.Bar>
        <S.Brand>
          Choose<S.BrandMark>Chow</S.BrandMark> ops
        </S.Brand>
        <S.Nav aria-label="Operations">
          {NAV.filter((item) => session.user?.role !== 'support' || !['/ops/promotions', '/ops/categories'].includes(item.path)).map((item) => (
            <S.NavLink variant="quiet" key={item.path} $active={isActive(item)} aria-current={isActive(item) ? 'page' : undefined} onClick={() => navigate(item.path)}>
              {item.label}
            </S.NavLink>
          ))}
        </S.Nav>
        <S.Spacer />
        <S.Who>
          {session.user.displayName} · {session.user.role}
        </S.Who>
        <S.Action onClick={session.signOut}>Sign out</S.Action>
      </S.Bar>
      <S.Main>
        <Routes>
          <Route index element={<OpsOverviewPage canManagePromotions={session.user.role !== 'support'} />} />
          <Route path="people" element={<OpsUsersPage canManage={session.user.role !== 'support'} />} />
          <Route path="kitchens" element={<OpsVendorsPage />} />
          <Route path="categories" element={session.user.role === 'support' ? <Navigate to="/ops" replace /> : <OpsCategoriesPage />} />
          <Route path="verification" element={<OpsVerificationPage />} />
          <Route path="orders" element={<OpsOrdersPage />} />
          <Route path="receivables" element={<OpsReceivablesPage />} />
          <Route path="support" element={<OpsSupportPage />} />
          <Route path="messages" element={<OpsMessagesPage />} />
          <Route path="promotions" element={session.user.role === 'support' ? <Navigate to="/ops" replace /> : <OpsPromotionsPage />} />
          <Route path="*" element={<Navigate to="/ops" replace />} />
        </Routes>
      </S.Main>
    </S.Page>
  )
}
